import styles from './MailboxExchangeSynchronization.module.scss';

import { Alert, Box, Button, Divider, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ContactExportModeCheckboxes } from '@work4all/components/lib/components/entity-preview/user-preview/components/ContactExportModeCheckboxes';
import { CheckboxRadioItem } from '@work4all/components/lib/input/checkbox-radio-item';
import { useHistoryStack } from '@work4all/components/lib/navigation/history-stack';
import { Caption } from '@work4all/components/lib/typography/caption/Caption';

import { UserSyncOptions } from '@work4all/models/lib/Classes/UserSyncOptions.entity';
import { MailboxFeatures } from '@work4all/models/lib/Enums/MailboxFeatures.enum';
import { SyncOptionsContactExport } from '@work4all/models/lib/Enums/SyncOptionsContactExport.enum';

import { useMailboxesData } from '../../../../../hooks/use-mailboxes-data';
import { MailboxPickerField } from '../../../../entity-picker/MailboxPickerField';
import { useMailboxFeatures } from '../hooks';
import { useUserSyncOptions } from '../hooks/use-user-sync-options';

export const MailboxExchangeSynchronization = () => {
  const { t } = useTranslation();

  const { syncOptions, mutateSyncOptions } = useUserSyncOptions();
  const [localOptions, setLocalOptions] =
    useState<UserSyncOptions>(syncOptions);

  const mailboxData = useMailboxesData();
  const mailboxFeatures = useMailboxFeatures();
  const chosenMailbox = useMemo(() => {
    return mailboxData.mailboxes?.find((x) => x.id === localOptions.mailboxId);
  }, [mailboxData.mailboxes, localOptions.mailboxId]);

  const missingMailboxFeature = useMemo(
    () =>
      !mailboxFeatures.hasFeature(
        chosenMailbox?.id,
        MailboxFeatures.READ_WRITE_CONTACTS
      ),
    [chosenMailbox?.id, mailboxFeatures]
  );
  const maskDisabled = useMemo(
    () => missingMailboxFeature || !chosenMailbox,
    [chosenMailbox, missingMailboxFeature]
  );

  useEffect(() => {
    setLocalOptions(syncOptions);
  }, [syncOptions]);

  const { goBack } = useHistoryStack();

  const updatedSettings = useMemo<string[]>(() => {
    return Object.keys(localOptions).filter(
      (key) => localOptions[key] !== syncOptions[key]
    );
  }, [localOptions, syncOptions]);

  const saveChanges = useCallback(async () => {
    await mutateSyncOptions(localOptions);
    goBack();
  }, [mutateSyncOptions, localOptions, goBack]);

  return (
    <>
      <Button
        className={styles.saveButton}
        color="primary"
        variant="contained"
        disabled={updatedSettings.length === 0}
        onClick={saveChanges}
      >
        {t('INPUTS.SAVE')}
      </Button>
      <Stack direction="column" spacing={2} padding="2.5rem">
        <Typography variant="h4">
          {t('EXCHANGE_SYNCHRONIZATION.TITLE')}
        </Typography>

        <div>
          <MailboxPickerField
            clearable
            value={
              chosenMailbox
                ? {
                    ...chosenMailbox,
                    smtpAddress: chosenMailbox?.mailboxPrimaryAddress,
                  }
                : null
            }
            onChange={(e) =>
              setLocalOptions({ ...localOptions, mailboxId: e?.id || null })
            }
          />
          {chosenMailbox && missingMailboxFeature && (
            <Caption color="error">
              {t('EXCHANGE_SYNCHRONIZATION.MAILBOX.TOOLTIP')}
            </Caption>
          )}
          {!chosenMailbox && (
            <Caption color="error">
              {t('EXCHANGE_SYNCHRONIZATION.CHOOSE_MAILBOX')}
            </Caption>
          )}
        </div>

        <div>
          <Divider>
            {t('EXCHANGE_SYNCHRONIZATION.CONTACT_EXPORT.TITLE')}
          </Divider>

          <CheckboxRadioItem
            control="checkbox"
            label={t('EXCHANGE_SYNCHRONIZATION.CONTACT_EXPORT.CUSTOMERS')}
            disabled={
              maskDisabled || !localOptions.contactsExportCustomerAllowed
            }
            checked={
              localOptions.contactsExportCustomerAllowed &&
              localOptions.contactsExportCustomer
            }
            onChange={() => {
              setLocalOptions({
                ...localOptions,
                contactsExportCustomer: !localOptions.contactsExportCustomer,
              });
            }}
          />

          <CheckboxRadioItem
            control="checkbox"
            label={t('EXCHANGE_SYNCHRONIZATION.CONTACT_EXPORT.SUPPLIERS')}
            disabled={
              maskDisabled || !localOptions.contactsExportSupplierAllowed
            }
            checked={
              localOptions.contactsExportSupplierAllowed &&
              localOptions.contactsExportSupplier
            }
            onChange={() => {
              setLocalOptions({
                ...localOptions,
                contactsExportSupplier: !localOptions.contactsExportSupplier,
              });
            }}
          />

          <CheckboxRadioItem
            control="checkbox"
            label={t('EXCHANGE_SYNCHRONIZATION.CONTACT_EXPORT.EMPLOYEES')}
            disabled={
              maskDisabled || !localOptions.contactsExportEmployeesAllowed
            }
            checked={
              localOptions.contactsExportEmployeesAllowed &&
              localOptions.contactsExportEmployees
            }
            onChange={() => {
              setLocalOptions({
                ...localOptions,
                contactsExportEmployees: !localOptions.contactsExportEmployees,
              });
            }}
          />
        </div>

        <div>
          <Divider>{t('EXCHANGE_SYNCHRONIZATION.CONTACT_TYPES.TITLE')}</Divider>

          <ContactExportModeCheckboxes
            mode={localOptions.contactsExportMode}
            maximumAllowedMode={localOptions.contactsExportAllowed}
            onChange={(mode) =>
              setLocalOptions({
                ...localOptions,
                contactsExportMode: mode,
              })
            }
            disabled={maskDisabled}
          />

          <CheckboxRadioItem
            control="checkbox"
            label={t(
              'EXCHANGE_SYNCHRONIZATION.CONTACT_TYPES.INCL_PRIVATE_ADDRESSES_AND_EMAIL_AND_TELEPHONE'
            )}
            checked={
              localOptions.contactsExportAllowed !==
                SyncOptionsContactExport.NO_EXPORT &&
              localOptions.contactsIcludePrivateData
            }
            onChange={() => {
              setLocalOptions({
                ...localOptions,
                contactsIcludePrivateData:
                  !localOptions.contactsIcludePrivateData,
              });
            }}
            disabled={
              maskDisabled ||
              localOptions.contactsExportAllowed ===
                SyncOptionsContactExport.NO_EXPORT
            }
          />
        </div>

        <div>
          <Divider>{t('EXCHANGE_SYNCHRONIZATION.APPOINTMENTS.TITLE')}</Divider>

          <CheckboxRadioItem
            control="checkbox"
            label={t(
              'EXCHANGE_SYNCHRONIZATION.APPOINTMENTS.ALLOW_SYNCHRONIZATION'
            )}
            checked={localOptions.appointmentsSync}
            onChange={() => {
              setLocalOptions({
                ...localOptions,
                appointmentsSync: !localOptions.appointmentsSync,
              });
            }}
            disabled={maskDisabled}
          />

          <CheckboxRadioItem
            label={t('USER.EXCHANGE.IMPORT_MANUAL')}
            checked={
              localOptions.appointmentsSync &&
              localOptions.appointmentsSyncExternalAppointments
            }
            onChange={() =>
              setLocalOptions({
                ...localOptions,
                appointmentsSyncExternalAppointments:
                  !localOptions.appointmentsSyncExternalAppointments,
              })
            }
            disabled={!localOptions.appointmentsSync || maskDisabled}
          />
          <Box pl="1rem">
            <CheckboxRadioItem
              control="checkbox"
              label={t(
                'EXCHANGE_SYNCHRONIZATION.APPOINTMENTS.EXCLUDE_PRIVATE_APPOINTMENTS'
              )}
              checked={
                localOptions.appointmentsSync &&
                localOptions.appointmentsSyncExternalAppointments &&
                !localOptions.appointmentsSyncPrivateItems
              }
              onChange={() => {
                setLocalOptions({
                  ...localOptions,
                  appointmentsSyncPrivateItems:
                    !localOptions.appointmentsSyncPrivateItems,
                });
              }}
              disabled={
                maskDisabled ||
                !localOptions.appointmentsSync ||
                !localOptions.appointmentsSyncExternalAppointments
              }
            />
          </Box>
        </div>
      </Stack>

      <div className={styles.bottomWrapper}>
        <Alert
          variant="filled"
          severity="error"
          color="warning"
          className={styles.alert}
        >
          {t('EXCHANGE_SYNCHRONIZATION.ALERT')}
        </Alert>
      </div>
    </>
  );
};
